// ============================================================
// links.js — SSOT de los enlaces de CTA (botones de acción) de los bloques.
//
// POR QUÉ EXISTE: un CTA solo navega en el sitio PUBLICADO si su render emite un `href`
// real. `data-link` es un atributo SOLO del editor (lo escribe editor/context-menu.js al
// "insertar link", lo lee core/extractor.js para persistir `cta_url`); por sí solo NO
// navega. Antes cada bloque lo resolvía a mano (o no lo hacía): los <button> y los <a> sin
// href no navegaban en publicado. Este util centraliza el saneo del href y el target/rel
// de los enlaces externos para que TODOS los bloques usen la MISMA lógica (DRY) y no
// diverjan. Reutiliza escapeHtml (SSOT de escape) de dom.js — no dupliques el regex ni el
// escape en cada bloque.
// ============================================================
import { escapeHtml } from './dom.js';

// Allowlist de esquemas/rutas navegables. Cualquier otro (javascript:, data:, …) NO pasa
// y cae a '#'. Complementa el sanitizer server-side (que también quita javascript:).
const _ESQUEMA_OK = /^(https?:\/\/|\/\/|\/|#|\?|mailto:|tel:)/i;
// Esquemas que salen del sitio → abren en pestaña nueva.
const _EXTERNO = /^(https?:\/\/|\/\/|mailto:|tel:)/i;

/**
 * href seguro para insertar en un atributo HTML: si la URL usa un esquema de la allowlist
 * (o es ruta relativa / ancla / query) la escapa y la devuelve; si no, cae a '#'.
 * NO hace encodeURIComponent (rompería `?phone=…&text=…`): solo escapa el atributo.
 */
export const hrefSeguro = (u) => {
    const v = String(u == null ? '' : u).trim();
    return _ESQUEMA_OK.test(v) ? escapeHtml(v) : '#';
};

// ── Enlaces a otra PÁGINA del sitio: `?page=<id>` y `?page=<id>#<ancla>` ─────────────────────
// La forma con ancla lleva a una SECCIÓN de otra página: la escribe el pie común
// (utils/pie-enlaces.js) cuando un `#contacto` de la home se pinta en una subpágina. Quien navega
// un `?page=` (viewer.js en editor/«Ver sitio»/SPA pública, public-init.js en el publicado,
// creator.js) lo parte con esta función: con `href.split('=')[1]` el ancla se colaba en el id
// ('home#contacto') y la página no se encontraba.

/** `?page=<id>[#<ancla>]` → `{ id, ancla }` (ancla '' si no hay); `null` si no es de página. */
export const destinoDePagina = (url) => {
    if (typeof url !== 'string' || url.indexOf('?page=') !== 0) return null;
    const resto = url.slice(6);
    const i = resto.indexOf('#');
    return i === -1 ? { id: resto, ancla: '' } : { id: resto.slice(0, i), ancla: resto.slice(i + 1) };
};

/** Inversa de `destinoDePagina`: la URL SPA de una página, con ancla si la hay. */
export const urlDePagina = (id, ancla) => `?page=${id}${ancla ? '#' + ancla : ''}`;

// ── Destino GLOBAL de los botones del sitio (`siteData.cta_destino`) ─────────────────────────
// Estado de RENDER (mismo patrón que `setRenderLang` de utils/i18n.js): lo fija la superficie que
// va a pintar —core/render.js en editor/preview y compileSiteHtml en pages/viewer.js para el
// publicado— con la URL ya resuelta para el idioma de esa página (utils/cta-destino.js).
//
// Se usa SOLO como fallback del `href` de los CTA sin enlace propio, y NUNCA se escribe en
// `data-link`: ese atributo es el enlace PROPIO del bloque y `core/extractor.js` lo persiste de
// vuelta en `<campo>_url` leyéndolo del DOM (también desde el autoguardado, que corre estés en
// modo edición o en "Ver Sitio"). Si el destino global viajara en `data-link` quedaría horneado
// como enlace propio de cada botón y cambiar el destino del sitio ya no actualizaría ninguno.
//
// Nunca alcanza a los enlaces de NAVEGACIÓN ni al formulario: la nav del header y los enlaces del
// pie se pintan con `hrefSeguro` directo (blocks/header.js, blocks/footer.js) y el submit del form
// es un <button>. Solo pasan por aquí los CTA de acción.
//
// El destino puede ser INTERNO (tipo 'reserva' de cta-destino.js: `?page=reservas`, `#bloque-…`,
// `/reservar`). Eso no necesita nada especial aquí, pero explica por qué el `target="_blank"` se
// decide por `_EXTERNO` y no por "hay destino global": un botón que lleva a la propia página de
// reservas debe abrirse en la MISMA pestaña, o el visitante pierde el sitio de vista a medio embudo.
let _destinoGlobal = '';

/** Fija la URL del destino global del sitio ('' = sin destino → comportamiento previo). */
export const setCtaDestinoGlobal = (url) => {
    _destinoGlobal = String(url == null ? '' : url).trim();
};

/** URL del destino global vigente ('' si no hay). */
export const getCtaDestinoGlobal = () => _destinoGlobal;

/**
 * Un `cta_url` vacío o '#' es un PLACEHOLDER (el render pinta '#' y el extractor lo devuelve), no
 * un enlace elegido por el cliente → es lo único que el destino global rellena.
 */
const _esPlaceholder = (v) => {
    const s = String(v == null ? '' : v).trim();
    return !s || s === '#';
};

/**
 * Atributos de navegación de un ancla de CTA a partir de la `cta_url` cruda del cliente:
 *   - href seguro: el enlace PROPIO del bloque; si no tiene (vacío o '#'), el DESTINO GLOBAL del
 *     sitio (ver arriba); y si tampoco hay, '#' (aspecto de "botón sin enlace", como siempre).
 *     El enlace propio SIEMPRE manda: el destino global es solo un fallback.
 *   - data-link con el valor saneado del enlace PROPIO (lo lee extractor.js para persistir
 *     cta_url; usar el valor saneado —igual que footer— evita persistir un `javascript:` en ambas
 *     superficies). NUNCA lleva el destino global: se hornearía como enlace propio del bloque.
 *   - target="_blank" rel="noopener noreferrer" SOLO si el enlace es externo (anti-tabnabbing).
 * Uso: `<a ${ctaAnchorAttrs(p.cta_url)} class="btn-action" data-field="cta" contenteditable="true">…</a>`
 * El BOTÓN SECUNDARIO (`cta2`, utils/boton-secundario.js) pasa `destinoGlobal: false`: el destino
 * global es la acción PRINCIPAL del sitio (reservar, WhatsApp…) y un «Saber más» que llevara ahí sería
 * un segundo botón principal. Sin enlace propio queda en '#', y `destino-boton.js` lo lleva a donde
 * dice su texto, como a cualquier botón sin enlace.
 * @param {string} ctaUrl  URL cruda (p.ej. p.cta_url / plan.cta_url).
 * @param {{editable?: boolean, destinoGlobal?: boolean}} [opts]  editable=false para anclas NO
 *        editables (omite data-link); destinoGlobal=false para no heredar el destino del sitio.
 * @returns {string} p.ej. `href="…" data-link="…" target="_blank" rel="noopener noreferrer"`
 */
export const ctaAnchorAttrs = (ctaUrl, opts = {}) => {
    const editable = opts.editable !== false;
    const raw = String(ctaUrl == null ? '' : ctaUrl).trim();
    const efectiva = _esPlaceholder(raw) ? (opts.destinoGlobal === false ? '' : _destinoGlobal) : raw;
    const href = hrefSeguro(efectiva);
    const target = _EXTERNO.test(efectiva) ? ' target="_blank" rel="noopener noreferrer"' : '';
    const dataLink = editable ? ` data-link="${hrefSeguro(raw)}"` : '';
    return `href="${href}"${dataLink}${target}`;
};
